import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';
import { Trophy, Star, Zap, Shield, Target, Flame, Crown, Award, Sparkles, Loader2 } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AppState } from '@/types';
import { useAiAction } from '@/hooks/useAiAction';

interface AchievementsProps {
  state: AppState;
}

interface MemberPoints {
  totalPoints: number;
  monthlyPoints: number;
  displayName: string;
}

const BADGE_DEFS = [
  { id: 'first_task', label: 'First Task', desc: 'Complete your first task', Icon: Target, threshold: '10 XP' },
  { id: 'getting_started', label: 'Getting Started', desc: 'Reach 100 XP', Icon: Zap, threshold: '100 XP' },
  { id: 'household_hero', label: 'Household Hero', desc: 'Reach 500 XP', Icon: Shield, threshold: '500 XP' },
  { id: 'legend', label: 'Legend', desc: 'Reach 1,000 XP', Icon: Flame, threshold: '1,000 XP' },
  { id: 'century', label: 'Century', desc: 'Reach 3,000 XP', Icon: Crown, threshold: '3,000 XP' },
  { id: 'mvp', label: 'MVP', desc: 'Lead the monthly leaderboard', Icon: Trophy, threshold: 'Monthly #1' },
  { id: 'dedicated', label: 'Dedicated', desc: 'Consistent household contributor', Icon: Star, threshold: '7+ days' },
  { id: 'top_performer', label: 'Top Performer', desc: 'Earn 5,000 XP total', Icon: Award, threshold: '5,000 XP' },
] as const;

export const Achievements: React.FC<AchievementsProps> = function Achievements({ state }: AchievementsProps) {
  const { t } = useTranslation();
  const householdId = state.currentHouseholdId;
  const userId = state.currentUser?.id ?? null;

  const [memberPoints, setMemberPoints] = useState<MemberPoints | null>(null);
  const [earnedAchievements, setEarnedAchievements] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [aiTip, setAiTip] = useState<{ tip: string; encouragement: string } | null>(null);
  const [tipLoading, setTipLoading] = useState(false);

  const { callAction } = useAiAction(householdId);

  useEffect(() => {
    async function fetchData() {
      if (!householdId || !userId) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch memberPoints doc
        const memberPointsRef = doc(db, 'households', householdId, 'memberPoints', userId);
        const memberPointsSnap = await getDoc(memberPointsRef);

        if (memberPointsSnap.exists()) {
          const data = memberPointsSnap.data();
          setMemberPoints({
            totalPoints: data.totalPoints ?? 0,
            monthlyPoints: data.monthlyPoints ?? 0,
            displayName: data.displayName ?? '',
          });
        } else {
          setMemberPoints(null);
        }

        // Fetch achievements subcollection
        const achievementsRef = collection(db, 'households', householdId, 'memberPoints', userId, 'achievements');
        const achievementsSnap = await getDocs(achievementsRef);

        const earnedIds = new Set<string>();
        achievementsSnap.forEach((doc) => {
          earnedIds.add(doc.id);
        });
        setEarnedAchievements(earnedIds);
      } catch {
        // Handle missing doc gracefully - no error shown
        setMemberPoints(null);
        setEarnedAchievements(new Set());
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [householdId, userId]);

  const totalPoints = memberPoints?.totalPoints ?? 0;
  const level = Math.max(1, Math.floor(Math.sqrt(totalPoints / 100)) + 1);
  const nextLevelXp = Math.pow(level, 2) * 100;
  const progress = Math.min(1, totalPoints / nextLevelXp);

  const handleGetTip = async () => {
    if (!householdId) return;
    setTipLoading(true);
    const result = await callAction<{ tip: string; encouragement: string }>('getMotivationTip', {
      totalPoints,
      level,
      tasksCompleted: Math.floor(totalPoints / 15),
    });
    setAiTip(result);
    setTipLoading(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!householdId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center gap-4">
        <Trophy className="w-16 h-16 text-slate-300 dark:text-slate-700" />
        <p className="text-slate-500 font-medium">{t('achievements.join_household', 'Join a household to track achievements.')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-page-narrow mx-auto space-y-8 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-2xl bg-brand/10">
          <Trophy className="w-7 h-7 text-brand" />
        </div>
        <div>
          <p className="text-sm text-slate-500">{t('achievements.subtitle', 'Your household contribution record')}</p>
        </div>
      </div>

      {/* XP Card */}
      <div className="bg-slate-900 dark:bg-slate-800 rounded-[2rem] p-6 space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-brand flex items-center justify-center text-white font-black text-2xl flex-shrink-0">
            {level}
          </div>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-slate-400">{t('achievements.level', 'Level {{level}}', { level })}</div>
            <div className="text-3xl font-black text-white">{totalPoints.toLocaleString()} XP</div>
            <div className="text-sm text-slate-400">{(memberPoints?.monthlyPoints ?? 0)} {t('achievements.pts_this_month', 'pts this month')}</div>
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-2">
            <span>{t('achievements.progress_to_level', 'Progress to Level {{level}}', { level: level + 1 })}</span>
            <span>{totalPoints} / {nextLevelXp} XP</span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all duration-500"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Achievement Badge Grid */}
      <div>
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-4">{t('achievements.badges', 'Badges')}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {BADGE_DEFS.map(({ id, label, desc, Icon, threshold }) => {
            const earned = earnedAchievements.has(id);
            return (
              <div
                key={id}
                className={`p-4 rounded-[1.5rem] border text-center space-y-2 transition-all ${
                  earned
                    ? 'bg-brand/10 border-brand/30'
                    : 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 opacity-50 grayscale'
                }`}
              >
                <Icon className={`w-8 h-8 mx-auto ${earned ? 'text-brand' : 'text-slate-400'}`} />
                <div className="text-sm font-black text-slate-900 dark:text-white">{label}</div>
                <div className="text-2xs text-slate-500">{desc}</div>
                <div className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full inline-block ${
                  earned ? 'bg-brand text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-400'
                }`}>
                  {earned ? t('achievements.earned', 'Earned') : threshold}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Motivation Tip */}
      {householdId && (
        <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-200 dark:border-slate-800 p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand" />
            <span className="text-sm font-black text-slate-900 dark:text-white">{t('achievements.ai_motivation', 'AI Motivation')}</span>
          </div>
          {aiTip ? (
            <div className="space-y-2">
              <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{aiTip.tip}</p>
              <p className="text-xs text-brand font-bold">{aiTip.encouragement}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">{t('achievements.ai_tip_desc', 'Get a personalized tip based on your progress.')}</p>
          )}
          <button
            onClick={handleGetTip}
            disabled={tipLoading}
            className="flex items-center gap-2 px-4 py-2 bg-brand text-white text-xs font-black rounded-full hover:bg-brand-hover transition-all disabled:opacity-50"
          >
            {tipLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {tipLoading ? t('achievements.generating', 'Generating...') : t('achievements.get_ai_tip', 'Get AI Tip')}
          </button>
        </div>
      )}
    </div>
  );
}